import { parseQuery } from '@calendly/http-tools'

import { generatePaletteCulori } from 'initializers/theme/generate_token_color_theme.js'

import { generateTextColor } from './generate_text_color'
import { generateColorShades } from './generate_color_shades'
import { normalizeHexColor } from './normalize_text_color'

const BACKGROUND_COLOR_DEFAULT = 'ffffff'
const PRIMARY_COLOR_DEFAULT = '006bff'
const TEXT_COLOR_DEFAULT = '0a2540'

const convertHexToRGB = (hex) => hex.match(/.{2}/g).map((x) => parseInt(x, 16))


const initTheme = ({ primaryColorHex, backgroundColorHex, textColorHex }) => {
  let variables = {}

  if (primaryColorHex || backgroundColorHex || textColorHex) {
    const colorPalette = generatePaletteCulori({
      backgroundColor: `#${backgroundColorHex ?? BACKGROUND_COLOR_DEFAULT}`,
      buttonColor: `#${primaryColorHex ?? PRIMARY_COLOR_DEFAULT}`,
      textColor: `#${textColorHex ?? TEXT_COLOR_DEFAULT}`
    })

    Object.entries(colorPalette).forEach(([name, value]) => {
      variables[`--${name}`] = value
    })
  }

  if (primaryColorHex) {
    const primaryColor = convertHexToRGB(primaryColorHex)
    const primaryColorRgb = primaryColor.join(', ')
    variables = {
      ...variables,
      '--primary-color': `rgb(${primaryColorRgb})`,
      '--primary-color-level2': `rgba(${primaryColorRgb}, 0.5)`,
      '--primary-color-level3': `rgba(${primaryColorRgb}, 0.15)`,
      '--primary-color-level4': `rgba(${primaryColorRgb}, 0.05)`,
      '--primary-text-color': generateTextColor(...primaryColor),
      '--primary-color-darker-10': generateColorShades(...primaryColor, -10),
      '--primary-color-darker-30': generateColorShades(...primaryColor, -30),
      '--text-color-secondary-4': generateTextColor(...primaryColor),
      '--text-color-secondary-3': `rgb(${primaryColorRgb})`
    }
  }
  if (backgroundColorHex) {
    const textColorRgb = '26, 26, 26'
    variables = {
      ...variables,
      '--container-background-color': `#${backgroundColorHex}`,
      '--color-bg-white-l-1': `#${backgroundColorHex}`,
      '--text-color': `rgb(${textColorRgb})`,
      '--text-color-level2': `rgba(${textColorRgb}, 0.61)`,
      '--text-color-level3': `rgba(${textColorRgb}, 0.1)`
    }
  }
  if (textColorHex) {
    const textColorRgb = convertHexToRGB(textColorHex).join(', ')
    variables = {
      ...variables,
      '--text-color': `rgb(${textColorRgb})`,
      '--text-color-level2': `rgba(${textColorRgb}, 0.6)`,
      '--text-color-level3': `rgba(${textColorRgb}, 0.1)`
    }
  }

  Object.entries(variables).forEach(([name, value]) => {
    document.documentElement.style.setProperty(name, value)
  })
}

const getThemeFromUrl = () => {
  const theme = parseQuery(window.location.search)
  const primaryColorHex = normalizeHexColor(theme.primary_color)
  const backgroundColorHex = normalizeHexColor(theme.background_color)
  const textColorHex = normalizeHexColor(theme.text_color)
  return { primaryColorHex, backgroundColorHex, textColorHex }
}

export const resetTheme = () => {
  const { primaryColorHex, backgroundColorHex, textColorHex } = getThemeFromUrl()

  // Reset theme if any theme is set in the URL
  if (primaryColorHex || backgroundColorHex || textColorHex) {
    initTheme({ primaryColorHex: PRIMARY_COLOR_DEFAULT, backgroundColorHex: BACKGROUND_COLOR_DEFAULT, textColorHex: TEXT_COLOR_DEFAULT })
  }
}

export const initThemeFromUrl = () => {
  const { primaryColorHex, backgroundColorHex, textColorHex } = getThemeFromUrl()

  if (primaryColorHex || backgroundColorHex || textColorHex) {
    initTheme({ primaryColorHex, backgroundColorHex, textColorHex })
  }
}
